/**
 * This store mainly handles all the data that the web-highlighter package produces and we use in the highlight implementation.
 * When a user interacts with the text in a document, the Note interface will pop up on the righthand side.
 * web-highlighter is a standalone npm package, and this store is supposed to be the 'glue' between that and our real state containing more data.
 */

import { Module, Mutation, VuexModule } from 'vuex-module-decorators';

// @ts-ignore
import Highlighter from 'web-highlighter';
import { NoteMeta } from '../types/noteMeta';

export const WEBHL_WRAPPER_TAG = 'webhl-mark';
export const HIGHLIGHT_CLASS = 'highlight';

// We instantiate the highlighter out here to ensure there is only one instance,
// and not an instance for each <SystimeHighlight>

@Module({
  stateFactory: true,
  namespaced: true
})
export default class WebHighlighterStore extends VuexModule {
  _currentId: string | null = null;

  _idToHighlightMap: Map<String, NoteMeta> = new Map<String, NoteMeta>();
  _noteIdToWebHLIdMap: Map<String, String> = new Map<String, String>();

  private readonly highlighterInstance: Highlighter =
    this.initializeHighlighter();

  private initializeHighlighter() {
    if (!this.highlighterInstance) {
      const highlighter = new Highlighter({
        wrapTag: WEBHL_WRAPPER_TAG,
        style: { className: HIGHLIGHT_CLASS }
        // exceptSelectors: ['div.ce-systimenews-newsfeed', 'p']
        // verbose: true
      });
      highlighter.on(Highlighter.event.HOVER, ({ id }: { id: string }) => {
        highlighter.addClass('highlight-hover', id);
      });
      highlighter.on(Highlighter.event.HOVER_OUT, ({ id }: { id: string }) => {
        highlighter.removeClass('highlight-hover', id);
      });
      return highlighter;
    }
    return this.highlighterInstance;
  }

  @Mutation
  setCurrentId(id: string | null) {
    this._currentId = id;
  }

  get currentId() {
    return this._currentId;
  }

  get highlighter(): Highlighter {
    return this.highlighterInstance;
  }

  get idMap(): Map<String, NoteMeta> {
    return this._idToHighlightMap;
  }

  get noteMap(): Map<String, String> {
    return this._noteIdToWebHLIdMap;
  }
}
